import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { cn } from "../cn";
import { Icon } from "./Icon";

/**
 * Form controls.
 *
 * These were a `.inp` CSS class applied to raw `<input>`, `<select>` and
 * `<textarea>` — which worked, but meant a control could be styled without
 * being labelled, and the focus ring lived in a stylesheet nobody looked at.
 * As components the accessible bits travel with the look.
 */

const BASE = cn(
  "w-full rounded-[10px] border border-line-strong bg-surface-2 px-3",
  "text-[13.5px] text-ink outline-none transition-colors",
  "placeholder:text-mute focus:border-acc",
  "disabled:cursor-not-allowed disabled:opacity-60",
);

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export function Input({ className, type, ...rest }: InputProps) {
  // Native date/time pickers render a light widget on our dark surface unless
  // the control declares its scheme. Doing it here means no caller has to
  // remember — one screen already had to, and the rest would have forgotten.
  const nativePicker = type === "date" || type === "datetime-local" || type === "time";
  return (
    <input
      type={type}
      className={cn(BASE, "h-[38px]", nativePicker && "[color-scheme:dark]", className)}
      {...rest}
    />
  );
}

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export function Textarea({ className, ...rest }: TextareaProps) {
  return <textarea className={cn(BASE, "min-h-[78px] py-2.5 leading-relaxed", className)} {...rest} />;
}

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export function Select({ className, ...rest }: SelectProps) {
  return (
    <select
      className={cn(
        BASE,
        "h-[38px] appearance-none bg-[position:right_12px_center] bg-no-repeat pr-8",
        // The chevron, drawn rather than imported so the control has no asset.
        "bg-[image:linear-gradient(45deg,transparent_50%,var(--color-mute)_50%),linear-gradient(135deg,var(--color-mute)_50%,transparent_50%)]",
        "bg-[size:5px_5px,5px_5px]",
        className,
      )}
      {...rest}
    />
  );
}

export interface SearchFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "type"> {
  value: string;
  /** Receives the value, not the event — every call site wanted the string. */
  onChange: (value: string) => void;
}

/** Search input with its leading icon. `type="search"` so a browser offers to
    clear it and screen readers announce it as a search box. */
export function SearchField({ value, onChange, className, ...rest }: SearchFieldProps) {
  return (
    <span
      className={cn(
        "flex h-[34px] w-[280px] items-center gap-[9px] rounded-lg",
        "border border-line bg-surface-2 px-[11px] text-[13px] text-mute",
        "focus-within:border-acc",
        className,
      )}
    >
      <Icon name="search" size={14} />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-ink outline-none placeholder:text-mute"
        {...rest}
      />
    </span>
  );
}
